import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import * as d3 from 'd3';
import { EmdChartBase } from '../emd-chart-base';
import { EmdTooltipRow } from '../emd-chart-tooltip.component';

export interface EmdDonutDatum {
  name: string;
  value: number;
  color: string;
}

@Component({
    selector: 'emd-donut-chart',
    templateUrl: './emd-donut-chart.component.html',
    styleUrls: ['./emd-donut-chart.component.scss'],
    standalone: false
})
export class EmdDonutChartComponent extends EmdChartBase {
  @Input() data: EmdDonutDatum[] = [];
  @Input() size = 260;
  @Input() ringRatio = 0.62;
  @Input() segmentPadAngle = 0;
  @Input() trackColor: string | null = null;
  @Input() centerValue: string | null = null;
  @Input() centerLabel: string | null = null;
  @Input() showTooltip = false;

  @Output() segmentClick = new EventEmitter<{ name: string; value: number; ctrlKey: boolean }>();

  @ViewChild('chart', { static: true }) chartRef!: ElementRef<HTMLDivElement>;

  tooltipVisible = false;
  tooltipRows: EmdTooltipRow[] = [];
  tooltipTitle = '';
  tooltipX = 0;
  tooltipY = 0;

  /** Center text swaps to the hovered slice, so the base values are kept. */
  displayValue = '';
  displayLabel = '';

  private highlighted: string[] | null = null;
  private focusIndex = -1;

  constructor(host: ElementRef<HTMLElement>) {
    super(host);
  }

  get hasData(): boolean {
    return this.data.some((d) => d.value > 0);
  }

  get total(): number {
    return this.data.reduce((sum, d) => sum + (d.value || 0), 0);
  }

  get ariaLabel(): string {
    if (!this.hasData) { return 'Donut chart, no data'; }
    const parts = this.data
      .filter((d) => d.value > 0)
      .map((d) => `${d.name} ${Math.round((d.value / this.total) * 100)} percent`);
    return `Donut chart. ${parts.join(', ')}`;
  }

  /**
   * Emphasise segments by name, dimming the rest; null or empty clears.
   * Patches the drawn arcs rather than redrawing so cross-filtering does not
   * flicker the ring.
   */
  highlightKeys(keys: string[] | null): void {
    this.highlighted = keys && keys.length ? keys : null;
    this.applyHighlight();
  }

  protected render(): void {
    const el = this.chartRef?.nativeElement;
    if (!el) { return; }

    d3.select(el).selectAll('svg').remove();
    this.resetCenterText();

    if (!this.hasData) {
      return;
    }

    // Design size normally; in a fullscreened card the ring may grow, capped
    // at 480px so the legend below keeps a sensible share of the screen.
    const size = Math.min(
      this.width || this.size,
      Math.max(this.size, Math.min(this.effectiveHeight(this.size), 480))
    );
    const radius = size / 2;
    const inner = radius * this.ringRatio;
    const outer = radius * 0.9;

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', size)
      .attr('height', size)
      .attr('viewBox', `0 0 ${size} ${size}`)
      .attr('role', 'presentation');

    const g = svg.append('g').attr('transform', `translate(${radius},${radius})`);

    // Original draws a filled disc of the full radius behind AND inside the
    // ring, not a stroked band.
    if (this.trackColor) {
      g.append('circle')
        .attr('r', radius)
        .attr('fill', this.trackColor)
        .attr('opacity', 1);
    }

    const pie = d3
      .pie<EmdDonutDatum>()
      .sort(null)
      .padAngle(this.segmentPadAngle)
      .value((d) => Math.max(d.value, 0));

    // The arc carries a constant padAngle(0)/cornerRadius(0) override, so the
    // pie's padAngle is neutralised and the ring renders as one seamless
    // annulus — the input contract is preserved but the gaps/rounding are not.
    const arc = d3.arc<any>().innerRadius(inner).outerRadius(outer).cornerRadius(0).padAngle(0);
    this.baseArc = arc;

    const arcs = pie(this.data.filter((d) => d.value > 0));

    const segments = g
      .selectAll('path.emd-donut-seg')
      .data(arcs)
      .enter()
      .append('path')
      .attr('class', 'emd-donut-seg')
      .attr('data-name', (d: any) => d.data.name)
      .attr('fill', (d: any) => d.data.color)
      .attr('d', (d: any) => arc(d) as string)
      .style('cursor', this.segmentClick.observers.length ? 'pointer' : 'default');

    segments
      .on('mouseenter', (event: MouseEvent, d: any) => {
        this.emphasise(d.data.name);
        if (this.showTooltip) { this.openTooltip(event, d.data); }
      })
      .on('mousemove', (event: MouseEvent) => {
        if (this.showTooltip) { this.positionTooltip(event); }
      })
      .on('mouseleave', () => {
        this.clearEmphasis();
        this.tooltipVisible = false;
      })
      .on('click', (event: MouseEvent, d: any) =>
        this.segmentClick.emit({
          name: d.data.name,
          value: d.data.value,
          ctrlKey: event.ctrlKey || event.metaKey,
        })
      );

    this.centerTextGroup = g;
    this.renderCenterText(g, radius);
    this.applyHighlight();
  }

  private centerTextGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;

  private renderCenterText(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    radius: number
  ): void {
    if (!this.centerValue && !this.centerLabel) { return; }

    g.append('text')
      .attr('class', 'emd-donut-center-value')
      .attr('text-anchor', 'middle')
      .attr('dy', this.centerLabel ? '-0.2em' : '0.35em')
      .style('font-size', `${Math.max(13, radius * 0.3)}px`)
      .text(this.displayValue);

    if (this.centerLabel) {
      g.append('text')
        .attr('class', 'emd-donut-center-label')
        .attr('text-anchor', 'middle')
        .attr('dy', '1.2em')
        .style('font-size', `${Math.max(9, radius * 0.14)}px`)
        .text(this.displayLabel);
    }
  }

  private setCenterText(value: string, label: string): void {
    this.displayValue = value;
    this.displayLabel = label;
    this.centerTextGroup?.select('.emd-donut-center-value').text(value);
    this.centerTextGroup?.select('.emd-donut-center-label').text(label);
  }

  private resetCenterText(): void {
    this.displayValue = this.centerValue ?? '';
    this.displayLabel = this.centerLabel ?? '';
    this.setCenterText(this.displayValue, this.displayLabel);
  }

  private baseArc?: d3.Arc<any, any>;

  // Hover signals with opacity alone — the original never changed arc geometry
  // on hover, so the ring outline stays a perfect circle at all times.
  private emphasise(name: string): void {
    d3.select(this.chartRef.nativeElement)
      .selectAll<SVGPathElement, any>('path.emd-donut-seg')
      .attr('opacity', (d: any) => (d.data.name === name ? 1 : 0.4));
  }

  private clearEmphasis(): void {
    this.applyHighlight();
  }

  private applyHighlight(): void {
    const active = this.highlighted;
    d3.select(this.chartRef?.nativeElement)
      .selectAll<SVGPathElement, any>('path.emd-donut-seg')
      .attr('opacity', (d: any) =>
        !active || active.indexOf(d.data.name) > -1 ? 1 : 0.25
      );
  }

  private openTooltip(event: MouseEvent, datum: EmdDonutDatum): void {
    const pct = this.total > 0 ? (datum.value / this.total) * 100 : 0;
    this.tooltipTitle = datum.name;
    // One line, matching the original: '27,954 · 13.3%', no swatch, no labels.
    this.tooltipRows = [
      { label: '', value: `${datum.value.toLocaleString('en-US')} · ${pct.toFixed(1)}%` },
    ];
    this.tooltipVisible = true;
    this.positionTooltip(event);
  }

  private positionTooltip(event: MouseEvent): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    this.tooltipX = event.clientX - rect.left;
    this.tooltipY = event.clientY - rect.top;
  }

  // ── Keyboard ────────────────────────────────────────────────────────────────

  onKeydown(event: KeyboardEvent): void {
    const named = this.data.filter((d) => d.value > 0);
    if (!named.length) { return; }

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        this.focusIndex = (this.focusIndex + 1) % named.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        this.focusIndex = (this.focusIndex - 1 + named.length) % named.length;
        break;
      case 'Enter':
      case ' ':
        if (this.focusIndex >= 0) {
          event.preventDefault();
          this.segmentClick.emit({
            name: named[this.focusIndex].name,
            value: named[this.focusIndex].value,
            ctrlKey: event.ctrlKey || event.metaKey,
          });
        }
        return;
      case 'Escape':
        this.focusIndex = -1;
        this.clearEmphasis();
        return;
      default:
        return;
    }

    event.preventDefault();
    this.emphasise(named[this.focusIndex].name);
  }

  onBlur(): void {
    this.focusIndex = -1;
    this.resetCenterText();
    this.applyHighlight();
  }
}
